import { ChevronLeft, Plus } from 'lucide-react';
import { authStoreUserTournamentId } from 'modules/auth/store/selectors';
import { MediaManagerTemplate } from 'modules/core/domain/interfaces/supabase/media-manager/entity';
import { useMediaManagerPresetsQuery } from 'modules/manager/store/media-manager-api';
import MediaManagerPresetCard from 'modules/manager/ui/components/media-manager-preset-card';
import { Pages } from 'modules/navigation/domain/enums/pages';
import React, { useCallback, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { ClipLoader } from 'react-spinners';
import { useIsAdmin } from 'shared/hooks/use-is-admin';
import { UDButton, UDText } from 'ud-ui/components';
import { useMainLayout } from 'modules/app-shell/ui/layout/context';
import UDBreadcrumbs from 'ud-ui/components/breadcrumbs';
import useCreateTemplateModal from '../hooks/modals/use-create-template-modal';
import useDeleteTemplateModal from '../hooks/modals/use-delete-template-modal';
import useDuplicateTemplateModal from '../hooks/modals/use-duplicate-template-modal';
import useEditTemplateModal from '../hooks/modals/use-edit-template-modal';

const MediaManagerPresetsPage: React.FC = () => {
  const { setHeaderContent } = useMainLayout();
  const navigate = useNavigate();
  const isAdmin = useIsAdmin();
  const tournamentId = useSelector(authStoreUserTournamentId);

  const { data: presets = [], isLoading, refetch } = useMediaManagerPresetsQuery();

  const breadcrumbs = useMemo(
    () => [
      { label: 'Медиа менеджер', to: Pages.MEDIA_MANAGER.INDEX },
      { label: 'Общие шаблоны', to: Pages.MEDIA_MANAGER.TEMPLATES_LIBRARY },
    ],
    [],
  );

  useEffect(() => {
    setHeaderContent(<UDBreadcrumbs breadcrumbs={breadcrumbs} />);
    return () => setHeaderContent(null);
  }, [breadcrumbs, setHeaderContent]);

  const loadPresets = useCallback(() => {
    void refetch();
  }, [refetch]);

  const openTemplate = useCallback(
    (template: MediaManagerTemplate) => navigate(Pages.MEDIA_MANAGER.builders.editor(template.id)),
    [navigate],
  );

  // A fresh copy lands in the tournament list — go there and let the page flash
  // the new row, so the user sees where it went.
  const onDuplicated = useCallback(
    (copy: MediaManagerTemplate) => {
      navigate(Pages.MEDIA_MANAGER.INDEX, { state: { highlightTemplateId: copy.id } });
    },
    [navigate],
  );

  const { modal: duplicateModal, open: openDuplicateModal, canDuplicate, isDuplicating } = useDuplicateTemplateModal({ onDuplicated });

  const existingNames = useMemo(() => presets.map((t) => t.name.trim()), [presets]);
  const { modal: createModal, open: openCreateModal } = useCreateTemplateModal({
    tournamentId: tournamentId ?? null,
    existingNames,
    templatesCount: presets.length,
    onCreated: loadPresets,
    createAsPreset: true,
  });
  const { modal: editModal, open: openEditModal } = useEditTemplateModal({
    templates: presets,
    onUpdated: loadPresets,
  });
  const { modal: deleteModal, open: openDeleteModal } = useDeleteTemplateModal({
    onDeleted: loadPresets,
  });

  return (
    <div className="flex h-full flex-col p-6">
      <div className="mb-4 flex items-center justify-between">
        <UDButton variant="secondary" className="flex items-center" onClick={() => navigate(Pages.MEDIA_MANAGER.INDEX)}>
          <ChevronLeft size={16} className="mr-2" />К шаблонам турнира
        </UDButton>
        {isAdmin && (
          <UDButton variant="dark" className="flex items-center" onClick={openCreateModal}>
            <Plus size={16} className="mr-2" />
            Создать пресет
          </UDButton>
        )}
      </div>

      {isLoading ? (
        <div className="flex h-full items-center justify-center">
          <ClipLoader />
        </div>
      ) : presets.length === 0 ? (
        <div className="flex h-full flex-col items-center justify-center gap-2">
          <UDText variant="body-2" className="text-gray-500">
            Пока нет общих шаблонов
          </UDText>
          {isAdmin && (
            <UDButton variant="primary" onClick={openCreateModal}>
              <Plus size={16} className="mr-2" /> Создать пресет
            </UDButton>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-4 md:grid-cols-3 xl:grid-cols-4">
          {presets.map((template) => (
            <MediaManagerPresetCard
              key={template.id}
              template={template}
              isAdmin={isAdmin}
              canDuplicate={canDuplicate}
              isDuplicating={isDuplicating}
              onOpen={openTemplate}
              onDuplicate={openDuplicateModal}
              onEdit={openEditModal}
              onDelete={openDeleteModal}
            />
          ))}
        </div>
      )}

      {createModal}
      {editModal}
      {deleteModal}
      {duplicateModal}
    </div>
  );
};

export default MediaManagerPresetsPage;
